import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  Input,
  Icon,
  Flex,
  Button,
  FormControl,
  FormLabel,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  useDisclosure,
  useToast,
  Spinner,
  Select,
  Skeleton,
  Badge,
  Tooltip,
  useColorModeValue,
  VStack,
  HStack,
  Divider,
  Grid,
  GridItem,
  IconButton,
} from '@chakra-ui/react';
import { AddIcon, DeleteIcon } from '@chakra-ui/icons';
import { MdTypeSpecimen, MdSchema } from 'react-icons/md';
import { FaFilter, FaKey, FaDatabase } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import baseUrl from 'Base_Url/baseUrl';

const MotionBox = motion(Box);
const MotionTr = motion(Tr);

function AddType() {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();

  const [types, setTypes] = useState([]);
  const [schemes, setSchemes] = useState([]);
  const [selectedSchemeId, setSelectedSchemeId] = useState('');
  const [typeName, setTypeName] = useState('');
  const [selectedFilterSchemeId, setSelectedFilterSchemeId] = useState('');
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [schemaDefinitions, setSchemaDefinitions] = useState([
    { key: '', type: '', required: true, ref: '' },
  ]);

  const token = localStorage.getItem('token');

  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const textColor = useColorModeValue('gray.800', 'white');
  const subtitleColor = useColorModeValue('gray.600', 'gray.300');
  const hoverBg = useColorModeValue('green.50', 'gray.700');
  const colorBg = useColorModeValue('purple.50', 'gray.700');
  const headerGradient = useColorModeValue(
    'linear(to-r, purple.500, pink.500)',
    'linear(to-r, purple.600, pink.600)',
  );
  const inputTextColor = useColorModeValue('gray.800', 'white');

  const typeOptions = ['String', 'Number', 'Date', 'Boolean', 'ObjectId'];

  const isValidKey = (key) => /^[a-zA-Z_][a-zA-Z0-9_]*$/.test(key);

  const fetchAllData = useCallback(
    async (schemeId = '') => {
      try {
        setFetching(true);
        const queryParams = new URLSearchParams();
        if (schemeId) queryParams.append('scheme', schemeId);
        const query = queryParams.toString();

        const [typeRes, schemeRes] = await Promise.all([
          fetch(`${baseUrl}/master/all-file-type${query ? `?${query}` : ''}`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          fetch(`${baseUrl}/master/all-scheme`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);

        const typeData = await typeRes.json();
        const schemeData = await schemeRes.json();

        if (typeRes.ok) {
          setTypes(typeData?.data?.data || []);
        } else {
          toast({
            title: 'Failed to fetch types',
            status: 'error',
            duration: 3000,
          });
        }

        if (schemeRes.ok) {
          const allSchemes = schemeData?.data?.data || [];
          setSchemes(allSchemes);
        } else {
          toast({
            title: 'Failed to fetch schemes',
            status: 'error',
            duration: 3000,
          });
        }
      } catch (err) {
        toast({
          title:
            err.response?.data?.message ||
            err.response?.data?.data ||
            'Error fetching data',
          status: 'error',
          duration: 3000,
        });
      } finally {
        setFetching(false);
      }
    },
    [token, toast],
  );

  useEffect(() => {
    fetchAllData(selectedFilterSchemeId);
  }, [selectedFilterSchemeId, fetchAllData]);

  const handleOpenModal = () => {
    if (schemes.length > 0) setSelectedSchemeId(schemes[0]._id);
    setSchemaDefinitions([{ key: '', type: '', required: true, ref: '' }]);
    onOpen();
  };

  const handleAddType = async () => {
    if (!typeName.trim() || !selectedSchemeId) {
      toast({
        title: 'Type name and scheme are required',
        status: 'warning',
        duration: 2000,
        isClosable: true,
      });
      return;
    }
    const invalidDefinition = schemaDefinitions.some(
      (def) => def.key.trim() === '' || def.type === '' || !isValidKey(def.key),
    );
    if (invalidDefinition) {
      toast({
        title:
          'All schema definitions must have valid key (starting with letter or underscore, containing only letters, numbers, underscores) and type selected',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${baseUrl}/master/add-file-type`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: typeName,
          scheme: selectedSchemeId,
          status: true,
          schemaDefinition: schemaDefinitions.map((def) => ({
            key: def.key,
            displayName: def.key,
            type: def.type,
            required: def.required,
            ...(def.type === 'ObjectId' ? { ref: def.key } : {}),
          })),
        }),
      });

      const data = await response.json();

      if (response.ok && data?.status) {
        toast({
          title: '✓ File Type Added',
          description: `${typeName} has been added successfully`,
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
        resetForm();
        onClose();
        fetchAllData(selectedFilterSchemeId);
      } else {
        toast({
          title: 'Failed to add type',
          description: data.message || 'Something went wrong',
          status: 'error',
          duration: 3000,
        });
      }
    } catch (error) {
      toast({
        title:
          error.response?.data?.message ||
          error.response?.data?.data ||
          'Error adding type',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setTypeName('');
    setSchemaDefinitions([{ key: '', type: '', required: true, ref: '' }]);
    if (schemes.length > 0) setSelectedSchemeId(schemes[0]._id);
  };

  const handleCloseModal = () => {
    resetForm();
    onClose();
  };

  const handleChange = (index, field, value) => {
    const updated = [...schemaDefinitions];
    updated[index][field] = value;
    setSchemaDefinitions(updated);
  };

  const handleAddRow = () => {
    const lastRow = schemaDefinitions[schemaDefinitions.length - 1];
    if (!lastRow.key.trim() || !lastRow.type) {
      toast({
        title: 'Please fill key and select type before adding a new row',
        status: 'warning',
        duration: 2000,
        isClosable: true,
      });
      return;
    }
    if (!isValidKey(lastRow.key)) {
      toast({
        title:
          'Key must start with a letter or underscore and contain only letters, numbers, and underscores',
        status: 'warning',
        duration: 2000,
        isClosable: true,
      });
      return;
    }
    setSchemaDefinitions([
      ...schemaDefinitions,
      { key: '', type: '', required: true, ref: '' },
    ]);
  };

  const handleRemoveRow = (index) => {
    if (schemaDefinitions.length === 1) {
      toast({
        title: 'At least one schema definition is required',
        status: 'warning',
        duration: 2000,
      });
      return;
    }
    setSchemaDefinitions(schemaDefinitions.filter((_, i) => i !== index));
  };

  return (
    <>
      <MotionBox
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.2 }}
      >
        <Box
          p={{ base: 4, md: 6 }}
          bg={cardBg}
          borderRadius="2xl"
          boxShadow="0 10px 40px rgba(0, 0, 0, 0.05)"
          border="1px solid"
          borderColor={borderColor}
          position="relative"
          overflow="hidden"
        >
          <Box
            position="absolute"
            top="-50px"
            right="-50px"
            w="150px"
            h="150px"
            borderRadius="full"
            bgGradient="linear(to-br, purple.100, pink.100)"
            opacity={0.1}
          />

          <Flex
            justify="space-between"
            align="center"
            mb={4}
            position="relative"
            zIndex={1}
          >
            <Flex align="center" gap={3}>
              <Flex
                align="center"
                justify="center"
                w="50px"
                h="50px"
                borderRadius="xl"
                bgGradient="linear(to-br, purple.500, pink.600)"
                boxShadow="0 8px 25px rgba(214, 188, 250, 0.3)"
              >
                <Icon as={MdTypeSpecimen} boxSize={6} color="white" />
              </Flex>
              <VStack align="start" spacing={0}>
                <Text fontSize="xl" fontWeight="bold" color={textColor}>
                  File Types
                </Text>
                <Text fontSize="sm" color={subtitleColor}>
                  Manage file type schemas
                </Text>
              </VStack>
            </Flex>

            <HStack>
              <Badge
                colorScheme="purple"
                fontSize="xs"
                px={3}
                py={1}
                borderRadius="full"
                display="flex"
                alignItems="center"
                gap={1}
              >
                <Text>{types.length}</Text>
                <Text>Total</Text>
              </Badge>
              {/* <Tooltip label="Add New File Type" hasArrow>
                <IconButton
                  colorScheme="purple"
                  size="md"
                  icon={<AddIcon />}
                  aria-label="Add File Type"
                  borderRadius="full"
                  bgGradient="linear(to-r, purple.500, pink.500)"
                  _hover={{
                    bgGradient: 'linear(to-r, purple.600, pink.600)',
                    transform: 'scale(1.1)',
                    boxShadow: '0 10px 20px rgba(214, 188, 250, 0.4)',
                  }}
                  transition="all 0.2s"
                  onClick={handleOpenModal}
                />
              </Tooltip> */}
            </HStack>
          </Flex>

          <Divider mb={4} />

          <Flex mb={6} position="relative" zIndex={1}>
            <FormControl width={{ base: '100%', md: '300px' }}>
              <Flex align="center" mb={2}>
                <Icon as={FaFilter} color="purple.500" mr={2} />
                <FormLabel
                  fontSize="sm"
                  fontWeight="medium"
                  color={textColor}
                  mb={0}
                >
                  Filter by Scheme
                </FormLabel>
              </Flex>
              <Select
                placeholder="All Schemes"
                value={selectedFilterSchemeId}
                onChange={(e) => setSelectedFilterSchemeId(e.target.value)}
                borderRadius="xl"
                border="2px solid"
                borderColor={borderColor}
                color={inputTextColor}
                _focus={{
                  borderColor: 'purple.500',
                  boxShadow: '0 0 0 3px rgba(168, 85, 247, 0.15)',
                }}
                _hover={{ borderColor: 'gray.300' }}
              >
                {schemes.map((scheme) => (
                  <option key={scheme._id} value={scheme._id}>
                    {scheme.name}
                  </option>
                ))}
              </Select>
            </FormControl>
          </Flex>

          <Box
            borderRadius="xl"
            overflow="hidden"
            border="1px solid"
            borderColor={borderColor}
            position="relative"
            zIndex={1}
          >
            {fetching ? (
              <Box p={6}>
                <VStack spacing={3} align="stretch">
                  {[...Array(5)].map((_, i) => (
                    <Flex key={i} gap={4}>
                      <Skeleton height="40px" width="50px" borderRadius="md" />
                      <Skeleton height="40px" flex="1" borderRadius="md" />
                      <Skeleton height="40px" flex="1" borderRadius="md" />
                    </Flex>
                  ))}
                </VStack>
              </Box>
            ) : types.length === 0 ? (
              <Flex
                direction="column"
                align="center"
                justify="center"
                p={10}
                minH="200px"
              >
                <Box
                  w="80px"
                  h="80px"
                  borderRadius="full"
                  bg="gray.100"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  mb={4}
                >
                  <Icon as={MdSchema} w={8} h={8} color="gray.400" />
                </Box>
                <Text
                  fontSize="lg"
                  fontWeight="medium"
                  color={textColor}
                  mb={2}
                >
                  No file types found
                </Text>
                <Text color={subtitleColor} textAlign="center" fontSize="sm">
                  {selectedFilterSchemeId
                    ? 'No file types for selected scheme'
                    : 'Start by adding your first file type'}
                </Text>
              </Flex>
            ) : (
              <Box overflow="auto" height={'60vh'}>
                <Table variant="simple">
                  <Thead bg={headerGradient}>
                    <Tr>
                      <Th
                        color="gray"
                        fontSize="sm"
                        fontWeight="bold"
                        py={4}
                        textAlign="center"
                      >
                        #
                      </Th>
                      <Th color="gray" fontSize="sm" fontWeight="bold" py={4}>
                        FILE TYPE NAME
                      </Th>
                      <Th color="gray" fontSize="sm" fontWeight="bold" py={4}>
                        SCHEME
                      </Th>
                      <Th
                        color="gray"
                        fontSize="sm"
                        fontWeight="bold"
                        py={4}
                        textAlign="center"
                      >
                        FIELDS
                      </Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    <AnimatePresence>
                      {types.map((type, index) => (
                        <MotionTr
                          key={type._id || index}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: 20 }}
                          transition={{ duration: 0.2 }}
                          _hover={{
                            bg: colorBg,
                            transition: 'background 0.2s',
                          }}
                        >
                          <Td
                            textAlign="center"
                            fontSize="sm"
                            fontWeight="medium"
                            color={textColor}
                            py={3}
                          >
                            <Badge
                              colorScheme="purple"
                              variant="subtle"
                              px={2}
                              py={1}
                              borderRadius="md"
                            >
                              {index + 1}
                            </Badge>
                          </Td>
                          <Td
                            fontSize="sm"
                            fontWeight="medium"
                            color={textColor}
                            py={3}
                          >
                            <HStack>
                              <Icon
                                as={MdTypeSpecimen}
                                color="purple.500"
                                boxSize={4}
                              />
                              <Text>{type.name}</Text>
                            </HStack>
                          </Td>
                          <Td fontSize="sm" color={subtitleColor} py={3}>
                            <Badge
                              colorScheme="green"
                              fontSize="xs"
                              px={2}
                              py={1}
                              borderRadius="md"
                            >
                              {type.scheme?.name || '—'}
                            </Badge>
                          </Td>
                          <Td
                            textAlign="center"
                            fontSize="sm"
                            color={subtitleColor}
                            py={3}
                          >
                            <Badge
                              colorScheme="blue"
                              fontSize="xs"
                              px={2}
                              py={1}
                              borderRadius="md"
                            >
                              {type.schemaDefinition?.length || 0} fields
                            </Badge>
                          </Td>
                        </MotionTr>
                      ))}
                    </AnimatePresence>
                  </Tbody>
                </Table>
              </Box>
            )}
          </Box>
        </Box>
      </MotionBox>

      <Modal isOpen={isOpen} onClose={handleCloseModal} isCentered size="2xl">
        <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
        <MotionBox
          as={ModalContent}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          borderRadius="2xl"
          overflow="hidden"
          boxShadow="0 20px 60px rgba(0, 0, 0, 0.3)"
          maxH="90vh"
          overflowY="auto"
        >
          <Box
            bgGradient="linear(to-r, purple.500, pink.600)"
            py={4}
            px={6}
            position="relative"
            overflow="hidden"
          >
            <Box
              position="absolute"
              top={0}
              right={0}
              bottom={0}
              left={0}
              bg="whiteAlpha.100"
              opacity={0.1}
            />
            <Flex align="center" position="relative" zIndex={1}>
              <Flex
                align="center"
                justify="center"
                w="48px"
                h="48px"
                borderRadius="xl"
                bg="whiteAlpha.900"
                mr={4}
              >
                <Icon as={MdSchema} boxSize={5} color="purple.600" />
              </Flex>
              <VStack align="start" spacing={0}>
                <ModalHeader
                  fontSize="xl"
                  fontWeight="bold"
                  color="white"
                  p={0}
                  mb={1}
                >
                  New File Type
                </ModalHeader>
                <Text fontSize="sm" color="whiteAlpha.800">
                  Define a new file type with schema
                </Text>
              </VStack>
            </Flex>
            <ModalCloseButton
              color="white"
              _hover={{ bg: 'whiteAlpha.200' }}
              _focus={{ boxShadow: 'none' }}
            />
          </Box>

          <ModalBody p={6} bg={useColorModeValue('gray.50', 'gray.900')}>
            <VStack spacing={6} align="stretch">
              {/* Basic Information */}
              <Box>
                <Text fontSize="sm" fontWeight="bold" color={textColor} mb={4}>
                  <HStack>
                    <Icon as={MdTypeSpecimen} color="purple.500" />
                    <Text>Basic Information</Text>
                  </HStack>
                </Text>
                <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={4}>
                  <FormControl isRequired>
                    <FormLabel fontSize="sm" color={subtitleColor}>
                      Scheme
                    </FormLabel>
                    <Select
                      value={selectedSchemeId}
                      onChange={(e) => setSelectedSchemeId(e.target.value)}
                      borderRadius="lg"
                      border="2px solid"
                      borderColor={borderColor}
                      _focus={{
                        borderColor: 'purple.500',
                        boxShadow: '0 0 0 3px rgba(168, 85, 247, 0.15)',
                      }}
                    >
                      {schemes.map((scheme) => (
                        <option key={scheme._id} value={scheme._id}>
                          {scheme.name}
                        </option>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl isRequired>
                    <FormLabel fontSize="sm" color={subtitleColor}>
                      File Type Name
                    </FormLabel>
                    <Input
                      placeholder="Enter file type name"
                      value={typeName}
                      onChange={(e) => setTypeName(e.target.value)}
                      borderRadius="lg"
                      border="2px solid"
                      borderColor={borderColor}
                      _focus={{
                        borderColor: 'purple.500',
                        boxShadow: '0 0 0 3px rgba(168, 85, 247, 0.15)',
                      }}
                    />
                  </FormControl>
                </Grid>
              </Box>

              {/* Schema Definition */}
              <Box>
                <Flex justify="space-between" align="center" mb={4}>
                  <Text fontSize="sm" fontWeight="bold" color={textColor}>
                    <HStack>
                      <Icon as={MdSchema} color="purple.500" />
                      <Text>Schema Definition</Text>
                    </HStack>
                  </Text>
                  <Tooltip label="Add Field" hasArrow>
                    <Button
                      size="sm"
                      colorScheme="purple"
                      variant="outline"
                      leftIcon={<AddIcon />}
                      onClick={handleAddRow}
                      borderRadius="lg"
                    >
                      Add Field
                    </Button>
                  </Tooltip>
                </Flex>

                <VStack spacing={3} align="stretch">
                  {schemaDefinitions.map((def, index) => (
                    <MotionBox
                      key={index}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <Box
                        p={4}
                        borderRadius="lg"
                        bg={cardBg}
                        border="1px solid"
                        borderColor={borderColor}
                        position="relative"
                      >
                        <Flex align="center" mb={3}>
                          <Icon as={FaKey} color="blue.500" mr={2} />
                          <Text
                            fontSize="sm"
                            fontWeight="medium"
                            color={textColor}
                          >
                            Field {index + 1}
                          </Text>
                          <Badge
                            ml={2}
                            colorScheme={def.required ? 'red' : 'green'}
                            fontSize="xs"
                          >
                            {def.required ? 'Required' : 'Optional'}
                          </Badge>
                          {schemaDefinitions.length > 1 && (
                            <IconButton
                              size="xs"
                              icon={<DeleteIcon />}
                              aria-label="Remove field"
                              colorScheme="red"
                              variant="ghost"
                              ml="auto"
                              onClick={() => handleRemoveRow(index)}
                            />
                          )}
                        </Flex>

                        <Grid
                          templateColumns={{ base: '1fr', md: '2fr 2fr 1fr' }}
                          gap={3}
                        >
                          <FormControl isRequired>
                            <FormLabel fontSize="xs" color={subtitleColor}>
                              Field Key
                            </FormLabel>
                            <Input
                              placeholder="e.g., firstName"
                              value={def.key}
                              onChange={(e) =>
                                handleChange(index, 'key', e.target.value)
                              }
                              size="sm"
                              borderRadius="md"
                              borderColor={
                                def.key && !isValidKey(def.key)
                                  ? 'red.500'
                                  : borderColor
                              }
                            />
                            {def.key && !isValidKey(def.key) && (
                              <Text fontSize="xs" color="red.500" mt={1}>
                                Invalid key format
                              </Text>
                            )}
                          </FormControl>

                          <FormControl isRequired>
                            <FormLabel fontSize="xs" color={subtitleColor}>
                              Data Type
                            </FormLabel>
                            <Select
                              value={def.type}
                              onChange={(e) =>
                                handleChange(index, 'type', e.target.value)
                              }
                              size="sm"
                              borderRadius="md"
                              placeholder="Select type"
                            >
                              {typeOptions.map((type) => (
                                <option key={type} value={type}>
                                  {type}
                                </option>
                              ))}
                            </Select>
                          </FormControl>

                          <FormControl>
                            <FormLabel fontSize="xs" color={subtitleColor}>
                              Required
                            </FormLabel>
                            <Select
                              value={def.required.toString()}
                              onChange={(e) =>
                                handleChange(
                                  index,
                                  'required',
                                  e.target.value === 'true',
                                )
                              }
                              size="sm"
                              borderRadius="md"
                            >
                              <option value="true">Yes</option>
                              <option value="false">No</option>
                            </Select>
                          </FormControl>
                        </Grid>
                      </Box>
                    </MotionBox>
                  ))}
                </VStack>
              </Box>
            </VStack>
          </ModalBody>

          <ModalFooter
            bg={useColorModeValue('gray.100', 'gray.800')}
            borderTop="1px solid"
            borderColor={borderColor}
            p={5}
          >
            <HStack spacing={3} w="full">
              <Button
                variant="outline"
                colorScheme="gray"
                flex="1"
                borderRadius="lg"
                onClick={handleCloseModal}
                _hover={{ transform: 'translateY(-1px)' }}
                transition="all 0.2s"
              >
                Cancel
              </Button>
              <Button
                colorScheme="purple"
                flex="1"
                borderRadius="lg"
                onClick={handleAddType}
                isLoading={loading}
                loadingText="Creating..."
                bgGradient="linear(to-r, purple.500, pink.500)"
                _hover={{
                  bgGradient: 'linear(to-r, purple.600, pink.600)',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 10px 20px rgba(214, 188, 250, 0.4)',
                }}
                _active={{ transform: 'translateY(0)' }}
                transition="all 0.2s"
                leftIcon={<AddIcon />}
              >
                Create File Type
              </Button>
            </HStack>
          </ModalFooter>
        </MotionBox>
      </Modal>
    </>
  );
}

export default AddType;
